<template>
  <v-dialog max-width="95vw" scrollable persistent v-model="showApplyVatForm">
    <v-card>
      <v-card-title class="secondary white--text mb-4 rounded-0">
        <span>Apply VAT to Delivery Advices</span>
        <span class="text-body-2 font-weight-regular ml-2">
          Search DAs, select the ones missing VAT, and apply the fixed rate on the DA rate.
        </span>

        <v-spacer></v-spacer>

        <v-chip color="warning" class="mr-4" label small>
          VAT 15% · fixed
        </v-chip>

        <v-btn icon @click="resetForm(), (showApplyVatForm = false)">
          <v-icon class="white--text">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form ref="vatForm" v-if="showApplyVatForm" v-model="isValid">
          <v-row class="mt-0">
            <v-col cols="12" sm="6" md="4" lg="3" class="Form-Columns">
              <InputField
                label="Search DA No."
                placeholder="e.g. DA024582, DA024555"
                :clearable="true"
                v-model="filters.search"
                @keyup.enter="searchList()"
              />
            </v-col>

            <v-col cols="12" md="6" lg="2" class="Form-Columns">
              <DateField
                :inputProps="{
                  dense: true,
                  label: 'Movement from',
                  clearable: true,
                  'hide-details': 'auto',
                }"
                v-model="filters.start_date"
                @change="delete filters.end_date"
              />
            </v-col>
            <v-col cols="12" md="6" lg="2" class="Form-Columns">
              <DateField
                :inputProps="{
                  dense: true,
                  label: 'Movement to',
                  clearable: true,
                  'hide-details': 'auto',
                }"
                :min="filters.start_date"
                v-model="filters.end_date"
              />
            </v-col>

            <v-col cols="12" lg="3" class="Form-Columns d-flex align-center">
              <v-btn depressed color="primary" class="mr-2" @click="searchList()">
                Search
              </v-btn>
              <v-btn depressed color="secondary" class="mr-2" @click="resetForm()">
                Reset
              </v-btn>
            </v-col>

            <v-col cols="12" class="pa-0">
              <v-divider></v-divider>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-text class="pt-0">
        <v-tabs height="35" v-model="tab" @change="onTabChange">
          <v-tabs-slider></v-tabs-slider>
          <v-tab>VAT Pending</v-tab>
          <v-tab>VAT Applied</v-tab>
          <v-tab>All</v-tab>
        </v-tabs>
      </v-card-text>

      <v-card-text>
        <div
          v-if="!hasSearched"
          class="d-flex align-center justify-center grey--text"
          style="height: 550px"
        >
          Search for Delivery Advices to apply VAT
        </div>
        <AgGridVue
          v-else
          style="width: 100%; height: 550px"
          class="ag-theme-alpine"
          id="applyVatGrid"
          :columnDefs="headers"
          :context="context"
          @grid-ready="onGridReady"
          rowSelection="multiple"
          @selection-changed="setSelectedRows"
          :defaultColDef="defaultColDef"
          :grid-options="gridOptions"
          :suppressDragLeaveHidesColumns="true"
          :rowData="allRowList"
        ></AgGridVue>

        <v-col v-if="hasSearched && totalItems > 0" cols="12" class="d-flex justify-end">
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="px-4 py-3">
        <div class="d-flex" style="gap: 32px">
          <div>
            <div class="text-caption grey--text">Selected DAs</div>
            <div class="text-h6 font-weight-bold">{{ selectedRows.length }}</div>
          </div>
          <div>
            <div class="text-caption grey--text">Rate total</div>
            <div class="text-h6 font-weight-bold">{{ rateTotal.toFixed(2) }}</div>
          </div>
          <div>
            <div class="text-caption grey--text">VAT to add</div>
            <div class="text-h6 font-weight-bold warning--text">{{ vatTotal.toFixed(2) }}</div>
          </div>
          <div>
            <div class="text-caption grey--text">New total</div>
            <div class="text-h6 font-weight-bold">{{ newTotal.toFixed(2) }}</div>
          </div>
        </div>

        <v-spacer></v-spacer>

        <v-btn depressed color="secondary" class="mr-2" @click="resetForm(), (showApplyVatForm = false)">
          Cancel
        </v-btn>
        <v-btn
          depressed
          color="warning"
          :disabled="!selectedRows.length"
          @click="submitForm()"
        >
          Apply 15% VAT to {{ selectedRows.length }} DAs
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import InputField from "@/components/FormBaseComponents/InputField.vue";
import SelectField from "@/components/FormBaseComponents/SelectField.vue";
import { AgGridVue } from "ag-grid-vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import Pagination from "@/components/General/Pagination.vue";

export default {
  components: {
    AgGridVue,
    InputField,
    SelectField,
    DateField,
    Pagination,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.tab = 0;
      } else {
        this.resetForm();
      }
    },
  },
  computed: {
    showApplyVatForm: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
    rateTotal() {
      return this.selectedRows.reduce((sum, da) => sum + Number(da.rate || 0), 0);
    },
    vatTotal() {
      return Number((this.rateTotal * 0.15).toFixed(4));
    },
    newTotal() {
      return this.rateTotal + this.vatTotal;
    },
  },
  data() {
    return {
      selectedRows: [],

      formErrors: {},
      isValid: true,
      allRowList: [],
      isSearchEnabled: false,
      hasSearched: false,
      tab: 0,

      filters: {},
      // pagination vars
      pageNo: 1,
      itemsPerPage: 100,
      totalItems: 0,

      // ag-grid vars
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        isRowSelectable: (rowNode) => {
          return !!(
            rowNode.data &&
            rowNode.data.payment_method === "Credit" &&
            Number(rowNode.data.tax_percentage) !== 15
          );
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
      headers: [
        {
          headerName: "DA No.",
          field: "da_no",
          minWidth: 60,
          checkboxSelection: (params) => {
            return !!(
              params.data &&
              params.data.payment_method === "Credit" &&
              Number(params.data.tax_percentage) !== 15
            );
          },
          headerCheckboxSelection: true,
        },
        {
          headerName: "Customer",
          field: "customer_name",
          minWidth: 120,
        },
        {
          headerName: "Service Type",
          field: "service_type",
          minWidth: 120,
        },
        {
          headerName: "Source → Mid",
          minWidth: 180,
          valueGetter: (params) => {
            if (!params.data) return "";
            return [params.data.source_name, params.data.mid_name]
              .filter(Boolean)
              .join(" → ");
          },
        },
        {
          headerName: "Movement",
          field: "movement_date_time",
          minWidth: 150,
        },
        {
          headerName: `Rate (${this.$globals.currency})`,
          field: "rate",
          minWidth: 100,
        },
        {
          headerName: "Current VAT",
          field: "tax_amount",
          minWidth: 100,
        },
        {
          headerName: "Status",
          minWidth: 100,
          valueGetter: (params) => {
            if (!params.data) return "";
            return Number(params.data.tax_percentage) === 15 ? "VAT Applied" : "No VAT";
          },
        },
      ],
    };
  },
  methods: {
    setSelectedRows() {
      this.selectedRows = this.gridApi.getSelectedRows();
    },
    resizeGrid() {
      if (this.gridOptions && "api" in this.gridOptions) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    onTabChange() {
      if (this.hasSearched) {
        this.searchList();
      }
    },
    searchList() {
      this.pageNo = 1;
      this.hasSearched = true;
      this.getDeliveryAdviceList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getDeliveryAdviceList();
    },
    prevPage() {
      this.pageNo--;
      this.getDeliveryAdviceList();
    },
    nextPage() {
      this.pageNo++;
      this.getDeliveryAdviceList();
    },
    resetForm() {
      this.allRowList = [];
      this.selectedRows = [];
      this.filters = {};
      this.pageNo = 1;
      this.tab = 0;
      this.hasSearched = false;
    },
    submitForm() {
      this.$bus.$emit("showLoader", true);
      this.formErrors = {};

      let params = {
        delivery_advices: this.selectedRows.map((da) => da.id),
      };

      this.$api.deliveryAdvice
        .bulkApplyVat(params)
        .then(() => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "VAT applied successfully!",
            color: "success",
          });
          this.resetForm();
          this.showApplyVatForm = false;
          this.$emit("refreshList");
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message:
              (err.data && err.data.message) || "Couldn't apply VAT to delivery advices!",
            color: "error",
          });
          this.formErrors = err.data;
        });
    },
    getDeliveryAdviceList() {
      this.$bus.$emit("showLoader", true);

      let { search, ...otherFilters } = this.filters;
      let params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...otherFilters,
      };
      if (search) {
        params.da_multi_search = search;
      }

      if (this.tab === 0) {
        params.missing_vat = true;
      } else if (this.tab === 1) {
        params.missing_vat = false;
      }

      this.$api.deliveryAdvice
        .getDaListForVat(params)
        .then((res) => {
          this.allRowList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
          this.selectedRows = [];
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.selectedRows = [];
          console.error(err);
        });
    },
  },
};
</script>
